// Pure camera math for the galaxy map: screen = world × scale + offset.

export interface Point {
  x: number;
  y: number;
}

export interface Camera {
  x: number;
  y: number;
  scale: number;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export const ZOOM_LIMITS = { min: 0.5, max: 80 } as const;

export function boundsOf(points: readonly Point[]): Bounds {
  if (points.length === 0) return { minX: -1, minY: -1, maxX: 1, maxY: 1 };
  return points.reduce(
    (b, p) => ({
      minX: Math.min(b.minX, p.x),
      minY: Math.min(b.minY, p.y),
      maxX: Math.max(b.maxX, p.x),
      maxY: Math.max(b.maxY, p.y),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    }
  );
}

export function fitCamera(
  bounds: Bounds,
  viewport: { width: number; height: number },
  padding = 32
): Camera {
  const width = Math.max(1, bounds.maxX - bounds.minX);
  const height = Math.max(1, bounds.maxY - bounds.minY);
  const scale = clampScale(
    Math.min((viewport.width - 2 * padding) / width, (viewport.height - 2 * padding) / height)
  );
  return {
    scale,
    x: viewport.width / 2 - (bounds.minX + width / 2) * scale,
    y: viewport.height / 2 - (bounds.minY + height / 2) * scale,
  };
}

export const clampScale = (scale: number): number =>
  Math.min(
    ZOOM_LIMITS.max,
    Math.max(ZOOM_LIMITS.min, Number.isFinite(scale) && scale > 0 ? scale : 1)
  );

export const screenToWorld = (camera: Camera, p: Point): Point => ({
  x: (p.x - camera.x) / camera.scale,
  y: (p.y - camera.y) / camera.scale,
});

export const worldToScreen = (camera: Camera, p: Point): Point => ({
  x: p.x * camera.scale + camera.x,
  y: p.y * camera.scale + camera.y,
});

/** Zooms by `factor` keeping the world point under `screenPoint` fixed. */
export function zoomAt(camera: Camera, screenPoint: Point, factor: number): Camera {
  const scale = clampScale(camera.scale * factor);
  const world = screenToWorld(camera, screenPoint);
  return { scale, x: screenPoint.x - world.x * scale, y: screenPoint.y - world.y * scale };
}

export const panBy = (camera: Camera, dx: number, dy: number): Camera => ({
  ...camera,
  x: camera.x + dx,
  y: camera.y + dy,
});

/** The system nearest to a screen point, if it lies within `radiusPx` on screen. */
export function pickNearest<T extends Point>(
  items: readonly T[],
  camera: Camera,
  screenPoint: Point,
  radiusPx: number
): T | null {
  const world = screenToWorld(camera, screenPoint);
  const maxWorld = radiusPx / camera.scale;
  let best: T | null = null;
  let bestDistance = maxWorld;
  for (const item of items) {
    const d = Math.hypot(item.x - world.x, item.y - world.y);
    if (d <= bestDistance) {
      best = item;
      bestDistance = d;
    }
  }
  return best;
}

/** A camera at `scale` with `point` in the middle of the viewport. */
export function centerOn(
  point: Point,
  viewport: { width: number; height: number },
  scale: number
): Camera {
  const s = clampScale(scale);
  return { scale: s, x: viewport.width / 2 - point.x * s, y: viewport.height / 2 - point.y * s };
}
